import _ from 'lodash';
import { transaction } from 'mobx';
import { useEffect, useState } from 'react';

import useStores from 'shared/hooks/use-stores';
import { useIsolatedCheckout } from 'checkout/isolated/context';
import { useCheckout } from 'checkout/context';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { useRouter } from 'next/compat/router';
import useCartDataFromServer from 'checkout/isolated/use-cart-data-from-server';
import { isValidHttpUrl } from 'shared/helpers/utils';

export default function useDataMap() {
  const router = useRouter();
  const stores = useStores();
  const isolatedCheckoutContext = useIsolatedCheckout();
  const checkoutContext = useCheckout();
  const { dispensary } = useDispensary();
  const serverCart = useCartDataFromServer(dispensary);
  const { checkoutId, r } = router?.query;
  const [hasLoaded, setHasLoaded] = useState(false);
  const pricingTypeMap = {
    medical: 'med',
    recreational: 'rec',
  };

  useEffect(() => {
    if (serverCart && dispensary && !hasLoaded) {
      transaction(() => {
        // clear the order completely before adding cart from server
        // this causes a warning from react that I wasn't able to solve
        // in a reasonable amount of time. TODO
        stores.cart.clearOrder();

        // when we have a server cart, populate it.
        // when we have a dispensary, populate it.
        stores.cart.setDispensary(dispensary);

        stores.cart.setOrderType(serverCart.orderType);

        // serverCarts use medical/recreational for pricingType
        // carts use med/rec for pricing type, use mapping to normalize
        stores.cart.changePricingType(pricingTypeMap[serverCart.pricingType]);

        // set the checkout token to be checkoutId to link tracked cart with client changes
        stores.cart.setCheckoutToken(checkoutId);

        // loop through server items and add them to the cart
        _.forEach(serverCart.items, (item) => {
          stores.cart.addItem(dispensary, item);
        });

        const { deliverable, geometry, street1, street2, city, state, valid, zip } = serverCart.address;
        const [lng, lat] = geometry?.coordinates || [];
        const residentialStatus = deliverable && valid ? 'VERIFIED' : 'UNVERIFIED';

        if (street1 && city && state && zip && !isolatedCheckoutContext.addressPopulatedFromServer) {
          const location = {
            lat,
            lng,
            ln1: street1,
            ln2: street2,
            city,
            state,
            zipcode: zip,
          };

          stores.cart.updateAddress({ location, residentialStatus });
          stores.UI.updateAddress({ location, residentialStatus });
          isolatedCheckoutContext.addressPopulatedFromServer = true;
        }

        // storing this on the checkout context instead of isolated checkout context
        // so we don't have to modify any of the components and pass isolated around.
        checkoutContext.checkoutId = checkoutId;

        if (r && isValidHttpUrl(r)) {
          checkoutContext.returnUrl = r;
        }

        // finish loading
        isolatedCheckoutContext.loading = false;

        setHasLoaded(true);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutId, dispensary, hasLoaded, serverCart]);

  return stores;
}
